import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import { SocketContext } from '../socket';
import { voiceApi, errorMessage } from './voiceApi';
import { useVoiceFeeds } from './useVoiceFeeds';
import { BUSY_STATUSES, whomText } from './callFormat';

/**
 * Owns, for this admin's browser:
 *   - the Twilio Voice Device and the single active call (placeCall / hangUp / mute / DTMF)
 *   - the dial target the widget is about to call (set by "Call" buttons anywhere in the app)
 *   - the shared activity feeds (recent + unassigned) every panel reads from
 *   - the one attach / retry path, so a change made in the bell, the dialog or the widget shows
 *     up everywhere at once
 *
 * Flow (docs/VOICE_CALLS_PLAN.md §5.1/§5.2):
 *   placeCall → POST /voice/calls (row created, student attached / label derived)
 *             → device.connect({ params: { callLogId, To } })
 *             → server's /voice/outbound stamps the CallSid and returns the <Dial>
 */
const VoiceContext = createContext(null);
export const useVoice = () => useContext(VoiceContext);

const CALLER_ID_KEY = 'voice.callerId';

export const VOICE_STATUS = {
    IDLE: 'idle',            // nothing going on
    PREPARING: 'preparing',  // creating the call_logs row / token
    CONNECTING: 'connecting',// browser leg connecting to Twilio
    RINGING: 'ringing',      // Twilio is dialing the destination
    IN_CALL: 'in_call',      // destination answered
    ENDED: 'ended',          // hung up (either side) — summary shown until reset
    ERROR: 'error',
};

// Twilio Voice SDK error codes for access-token problems (expired, invalid, not yet valid).
const TOKEN_ERROR_CODES = new Set([20101, 20103, 20104, 20105, 20106, 20107, 20151, 20157]);
const isTokenError = (err) => {
    const code = err?.code ?? err?.originalError?.code;
    if (TOKEN_ERROR_CODES.has(Number(code))) return true;
    const msg = String(err?.message || '').toLowerCase();
    return msg.includes('token') && (msg.includes('expire') || msg.includes('invalid'));
};

export function VoiceProvider({ children }) {
    const [me, setMe] = useState(null);         // GET /voice/me
    const [meError, setMeError] = useState(null);
    const [status, setStatus] = useState(VOICE_STATUS.IDLE);
    const [callLog, setCallLog] = useState(null);         // row created for the current/last call
    const [candidates, setCandidates] = useState([]);     // several students share the dialed number
    const [muted, setMuted] = useState(false);
    const [startedAt, setStartedAt] = useState(null);     // ms timestamp when the callee answered
    const [endedAt, setEndedAt] = useState(null);         // ms timestamp when the call ended — the summary timer stops here
    const [error, setError] = useState(null);
    const [widgetOpen, setWidgetOpen] = useState(false);
    const [target, setTarget] = useState(null);           // { to_number, student, contact_label, missed_call_id?, missed_label? } | null
    const [callerId, setCallerIdState] = useState(() => { try { return localStorage.getItem(CALLER_ID_KEY) || null; } catch { return null; } });

    const deviceRef = useRef(null);
    const callRef = useRef(null);
    const cancelRef = useRef(false);   // hang-up pressed while still preparing/connecting
    const dropAfterCallRef = useRef(false); // permission revoked mid-call: drop the Device once the call ends
    const socket = useContext(SocketContext);

    const permitted = Boolean(me?.permitted);
    const manager = Boolean(me?.manager);   // listed in VOICE_ADMIN_EMAILS: grants the permission, opens any call
    const canCall = Boolean(me?.permitted && me?.configured && me?.enabled);
    const busy = BUSY_STATUSES.includes(status);
    const inCall = status === VOICE_STATUS.IN_CALL;

    // Every admin sees the feeds (cards only); they load once the admin is known. The unassigned
    // list is fetched only while the bell's Unassigned tab is open.
    const [unassignedWanted, setUnassignedWanted] = useState(false);
    const feeds = useVoiceFeeds({ enabled: Boolean(me), pollMs: 60000, unassignedWanted });
    const { mergeCard } = feeds;

    useEffect(() => {
        let cancelled = false;
        voiceApi.me()
            .then((data) => { if (!cancelled) setMe(data); })
            .catch((err) => { if (!cancelled) setMeError(errorMessage(err)); });
        return () => { cancelled = true; };
    }, []);

    /** Destroy the Device (if any) so the next call creates a fresh one with a new token. */
    const dropDevice = useCallback(() => {
        const device = deviceRef.current;
        deviceRef.current = null;
        if (device) { try { device.destroy(); } catch { /* ignore */ } }
    }, []);

    // Tear everything down with the layout (sign-out / navigation away from the admin area).
    useEffect(() => () => {
        if (callRef.current) { try { callRef.current.disconnect(); } catch { /* ignore */ } }
        dropDevice();
    }, [dropDevice]);

    const callerIds = useMemo(() => me?.caller_ids || [], [me]);
    const effectiveCallerId = callerIds.includes(callerId) ? callerId : (me?.default_caller_id || null);
    const setCallerId = useCallback((value) => {
        setCallerIdState(value);
        try { if (value) localStorage.setItem(CALLER_ID_KEY, value); else localStorage.removeItem(CALLER_ID_KEY); } catch { /* ignore */ }
    }, []);

    /** Create (once) the Twilio Device with a fresh access token; refresh the token before expiry. */
    const ensureDevice = useCallback(async () => {
        if (deviceRef.current) return deviceRef.current;
        const { token } = await voiceApi.token();
        const device = new Device(token, {
            codecPreferences: ['opus', 'pcmu'],
            logLevel: 'error',
            closeProtection: 'A call is in progress. Leave the page and end it?',
        });
        device.on('tokenWillExpire', async () => {
            try { const fresh = await voiceApi.token(); device.updateToken(fresh.token); }
            catch (err) { console.warn('[voice] token refresh failed:', errorMessage(err)); }
        });
        device.on('error', (err) => {
            console.warn('[voice] device error:', err);
            setError(err?.message || 'Softphone error');
            if (isTokenError(err)) dropDevice();
        });
        deviceRef.current = device;
        return device;
    }, [dropDevice]);

    /**
     * Tell the server a call never got off the ground, so its row closes instead of sitting at "Connecting"
     * until the nightly sweep. Best effort: the call already failed, and failing to report that must not
     * replace the error the admin is looking at.
     */
    const abandon = useCallback((callLogId, reason) => {
        if (!callLogId) return;
        voiceApi.abandonCall(callLogId, reason).catch(() => {});
    }, []);

    const finishCall = useCallback(() => {
        callRef.current = null;
        setMuted(false);
        setEndedAt((prev) => prev || Date.now()); // disconnect / cancel / reject can each fire; keep the first
        setStatus((prev) => (prev === VOICE_STATUS.ERROR ? prev : VOICE_STATUS.ENDED));
        if (dropAfterCallRef.current) { dropAfterCallRef.current = false; dropDevice(); }
    }, [dropDevice]);

    // A manager granted or revoked this admin's call permission: refresh /voice/me now rather than
    // at the next page load. On revoke the Twilio Device is dropped at once when idle, or as soon as
    // the current call ends (a call in progress is never cut).
    useEffect(() => {
        if (!socket) return undefined;
        const onPermission = (payload) => {
            if (!payload || payload.admin_uuid !== me?.admin?.uuid) return;
            voiceApi.me().then(setMe).catch(() => {});
            if (payload.can_voice_call === false) {
                if (callRef.current) dropAfterCallRef.current = true; else dropDevice();
            }
        };
        socket.on('voice.permission', onPermission);
        return () => socket.off('voice.permission', onPermission);
    }, [socket, me?.admin?.uuid, dropDevice]);

    /**
     * Place a call. `to_number` in any typed format; the server normalises it.
     * Pass `student_uuid` + `contact_label` when calling from a student record (and `also_student_uuids`
     * to file the call under other students too), or `teacher_uuid` when calling a teacher.
     * `missed_call_id` comes only from a Missed Calls row's call icon: the server links the call to that row.
     */
    const placeCall = useCallback(async ({ to_number, student_uuid, student_id, teacher_uuid, contact_label, from_number, also_student_uuids, missed_call_id } = {}) => {
        if (callRef.current || BUSY_STATUSES.includes(status)) throw new Error('A call is already in progress');
        if (!canCall) throw new Error('Voice calling is not available for this account');
        setError(null);
        setCandidates([]);
        setStartedAt(null);
        setEndedAt(null);
        setStatus(VOICE_STATUS.PREPARING);
        cancelRef.current = false;
        let created = null;   // the row, once the server has it — so a later failure can close it
        try {
            const body = { to_number };
            const from = from_number || effectiveCallerId;
            if (from) body.from_number = from;
            if (contact_label) body.contact_label = contact_label;
            if (teacher_uuid) body.teacher_uuid = teacher_uuid;
            else if (student_uuid) body.student_uuid = student_uuid;
            else if (student_id) body.student_id = student_id;
            if (!teacher_uuid && Array.isArray(also_student_uuids) && also_student_uuids.length && (student_uuid || student_id)) body.also_student_uuids = also_student_uuids;
            if (missed_call_id != null) body.missed_call_id = missed_call_id;

            const data = await voiceApi.createCall(body);
            created = data.call_log;
            setCallLog(data.call_log);
            setCandidates([
                ...(data.student_candidates || []).map((c) => ({ ...c, kind: 'student' })),
                ...(data.teacher_candidates || []).map((c) => ({ ...c, kind: 'teacher' })),
            ]);
            if (cancelRef.current) { setStatus(VOICE_STATUS.ENDED); return data.call_log; } // hung up while preparing

            const device = await ensureDevice();
            setStatus(VOICE_STATUS.CONNECTING);
            const call = await device.connect({
                params: { callLogId: String(data.call_log.id), To: data.call_log.to_number },
            });
            if (cancelRef.current) { // hung up while connecting: end it the moment it exists
                try { call.disconnect(); } catch { /* ignore */ }
                setStatus(VOICE_STATUS.ENDED);
                return data.call_log;
            }
            callRef.current = call;
            call.on('ringing', () => setStatus(VOICE_STATUS.RINGING));
            call.on('accept', () => { setStatus(VOICE_STATUS.IN_CALL); setStartedAt(Date.now()); });
            call.on('disconnect', finishCall);
            call.on('cancel', finishCall);
            call.on('reject', finishCall);
            call.on('error', (err) => {
                setError(err?.message || 'Call error');
                setStatus(VOICE_STATUS.ERROR);
                abandon(data.call_log.id, err?.message || 'Call error');
                finishCall();
            });
            return data.call_log;
        } catch (err) {
            // 409: this admin already has a live call, on another tab or window — name it.
            const live = err?.response?.status === 409 ? err.response.data?.live_call : null;
            setError(live ? `You already have a call in progress with ${whomText(live)} (#${live.id}), on another tab or window. Finish it first.` : errorMessage(err));
            setStatus(VOICE_STATUS.ERROR);
            // The row exists but the call never got off the ground (the device failed, the server was
            // unreachable). Twilio will never send a callback for a call that never started, so close it here
            // or the activity card sits at "Connecting" until the nightly sweep.
            if (created && !live) abandon(created.id, errorMessage(err));
            callRef.current = null;
            if (isTokenError(err)) dropDevice();
            throw err;
        }
    }, [status, canCall, effectiveCallerId, ensureDevice, finishCall, dropDevice, abandon]);

    const hangUp = useCallback(() => {
        const call = callRef.current;
        if (call) { try { call.disconnect(); } catch { /* ignore */ } return; }
        if (status === VOICE_STATUS.PREPARING || status === VOICE_STATUS.CONNECTING) {
            cancelRef.current = true; // placeCall checks this after each await and ends the call
            setStatus(VOICE_STATUS.ENDED);
        }
    }, [status]);

    const toggleMute = useCallback(() => {
        const call = callRef.current;
        if (!call) return;
        const next = !call.isMuted();
        call.mute(next);
        setMuted(next);
    }, []);

    const sendDigits = useCallback((digits) => {
        const call = callRef.current;
        if (call) call.sendDigits(String(digits));
    }, []);

    /**
     * The one attach path: PATCH the call, then push the result into the feeds and, if it is the
     * widget's current call, into callLog. Used by the bell, the dialog and the widget.
     */
    const attachCall = useCallback(async (id, { student_uuid, student_id, teacher_uuid, teacher_id, contact_label, also_student_uuids } = {}) => {
        const body = {};
        if (student_uuid) body.student_uuid = student_uuid;
        else if (student_id !== undefined) body.student_id = student_id; // null = detach (clears the label, the extras and the memo)
        if (teacher_uuid) body.teacher_uuid = teacher_uuid;              // attaching a teacher detaches the student
        else if (teacher_id !== undefined) body.teacher_id = teacher_id; // null = detach the teacher
        if (contact_label) body.contact_label = contact_label;
        if (Array.isArray(also_student_uuids)) body.also_student_uuids = also_student_uuids; // the full list; [] clears
        const { call_log } = await voiceApi.updateCall(id, body);
        mergeCard(call_log);
        setCallLog((prev) => (prev && prev.id === id ? call_log : prev));
        setCandidates((prev) => (callLog && callLog.id === id ? [] : prev));
        return call_log;
    }, [mergeCard, callLog]);

    /** The one retry path (recording copy or transcription). Returns { action, call_log }. */
    const retryCall = useCallback(async (id) => {
        const data = await voiceApi.retry(id);
        mergeCard(data.card);
        setCallLog((prev) => (prev && prev.id === id ? data.call_log : prev));
        return data;
    }, [mergeCard]);

    /** Save notes on a call (dialog). */
    const saveNotes = useCallback(async (id, notes) => {
        const { call_log } = await voiceApi.updateCall(id, { notes });
        mergeCard(call_log);
        setCallLog((prev) => (prev && prev.id === id ? call_log : prev));
        return call_log;
    }, [mergeCard]);

    const reset = useCallback(() => {
        if (callRef.current) return; // never wipe an active call
        setStatus(VOICE_STATUS.IDLE);
        setCallLog(null);
        setCandidates([]);
        setStartedAt(null);
        setEndedAt(null);
        setError(null);
        setTarget(null);
    }, []);

    /** Open the widget with a number (and student) to dial — the "Call" buttons on student cards. */
    const openDialer = useCallback((next = null) => {
        if (!callRef.current && !BUSY_STATUSES.includes(status)) {
            reset();
            setTarget(next);
        }
        setWidgetOpen(true);
    }, [reset, status]);

    const value = useMemo(() => ({
        me, meError, permitted, manager, canCall,
        callerIds, callerId: effectiveCallerId, setCallerId,
        status, busy, inCall, callLog, candidates, muted, startedAt, endedAt, error,
        placeCall, hangUp, toggleMute, sendDigits, reset,
        attachCall, retryCall, saveNotes,
        feeds, setUnassignedWanted,
        widgetOpen, setWidgetOpen, openDialer, target, setTarget,
        refreshMe: () => voiceApi.me().then(setMe),
    }), [me, meError, permitted, manager, canCall, callerIds, effectiveCallerId, setCallerId, status, busy, inCall, callLog, candidates, muted, startedAt, endedAt, error,
        placeCall, hangUp, toggleMute, sendDigits, reset, attachCall, retryCall, saveNotes, feeds, widgetOpen, openDialer, target]);

    return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
}

export default VoiceProvider;
